// src/components/RebeccaTopicsDashboard.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  tryToFetchTopicTaxonomy,
  tryToGetFeedbackTopicStats,
  tryToSearchFeedbackTopics,
  tryToFetchFeedbackExecByCategory,
} from '../../../../store/admin/topics/feedbackTopicsSlice';
import type { ExecStatsFields, ExecByCategoryData } from '../../../../store/admin/topics/feedbackTopicsSlice';
import { AppDispatch, RootState } from '../../../../app/store';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { useTheme } from '../../../../utils/theme-context';
import SelectCheckbox from '../../../../shared/select-checkbox';

interface FeedbackDoc {
  _id: string;

  description?: string;

  createdBy?: {
    id?: string;
    username?: string;
    role?: string;
    type?: string;
  };

  type?: 'staff' | 'guest' | string;

  companySub?: string;

  additionalInfo?: {
    category?: string | null;
    subCategory?: string | null;

    tripId?: string;
    itineraryId?: string;
    journeyTitle?: string;

    cabin?: string | null;
    sentiment?: 'positive' | 'negative' | 'neutral' | string;

    [k: string]: any;
  };

  action?: boolean;
  actionStatus?: string;
  files?: any[];

  createdAt?: string;
  updatedAt?: string;
}


type Taxonomy = {
  categories: string[];
  subcategories: Record<string, string[]>;
};

interface AssignmentStats {
  total: number;
  categories: { category: string | null; count: number; pct: number }[];
  subcategories: {
    category: string | null;
    subCategory: string | null;
    count: number;
    pctOfCategory: number;
    pctOfTotal: number;
  }[];
  unassigned: { count: number; pct: number };
}

/* ─── Stat Cards Sub-Components ─── */
const StatCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className='rounded-xl border border-gray-200 bg-white p-5 dark:bg-[#111317] dark:border-white/10'>
    <div className='text-[#717680] dark:text-white !font-arial font-normal text-[13px] mb-6'>{title}</div>
    {children}
  </div>
);

const ProgressBar: React.FC<{
  label: string;
  count: number;
  pct: number;
  color: string;
  maxPct: number;
}> = ({ label, count, pct, color, maxPct }) => (
  <div className='mb-8'>
    <div className='flex flex-row justify-between mb-2'>
      <div className=' text-[#717680] dark:text-white mb-1 text-[13px] font-arial font-normal truncate'>
        {label} ({count})
      </div>
      <span className=' text-[#414651] dark:text-white font-bold text-[12px] font-plus-jakarta w-16 text-right'>{pct.toFixed(2)}%</span>
    </div>

    <div className='flex items-center gap-2'>
      <div className='flex-1  bg-[#F3F4F6] dark:bg-[#000] rounded-full overflow-hidden h-[6px]'>
        <div
          className='h-full rounded-full transition-all duration-500'
          style={{
            width: `${maxPct > 0 ? (pct / maxPct) * 100 : 0}%`,
            backgroundColor: color,
          }}
        />
      </div>

    </div>
  </div>
);


const CategoryExecCards: React.FC<{ data: ExecStatsFields; label?: string }> = ({ data, label }) => {
  const statusMaxPct = Math.max(
    data.totalAllocatedAndAcknowledgedPct,
    data.totalAllocatedButNotAcknowledgedPct,
    data.totalUnallocatedPct,
  );

  const sentBarMaxPct = Math.max(
    data.positiveSentimentPct,
    data.neutralSentimentPct,
    data.negativeSentimentPct,
  );


  return (
    <div className='mt-6'>
      {label && (
        <div className='text-[16px] font-bold text-[#1E293B] dark:text-white/70 font-arial mb-3'>
          Category Sentiment breakdown
          <span className='ml-2 font-normal text-gray-500'>— {label}</span>
        </div>
      )}
      <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
        {/* Column 1: Total Feedback + Sentiment counts stacked */}
        <div className='flex flex-col gap-4'>
          <StatCard title='Total Feedback'>
            <div className='flex justify-between'>
              <div>
                <div className='text-2xl  text-center text-[24px] font-plus-jakarta font-bold text-[#1E293B] dark:text-white'>{data.totalByGuest}</div>
                <div className='text-xs  text-center text-[10px] font-plus-jakarta font-medium text-[#717680] dark:text-white/70'>By Guest</div>
              </div>
              <div>
                <div className='text-2xl text-center text-[24px] font-plus-jakarta font-bold text-[#1E293B] dark:text-white'>{data.totalByStaff}</div>
                <div className='text-xs text-center text-[10px] font-plus-jakarta font-medium text-[#717680] dark:text-white/70'>By Staff</div>
              </div>
            </div>
          </StatCard>

          <StatCard title='Sentiment of feedback'>
            <div className='flex justify-between'>
              <div>
                <div className=' text-[#00C950] text-center font-normal text-[14px] font-plus-jakarta mb-2'>Positive</div>
                <div className='text-center text-[#1E293B] dark:text-white font-plus-jakarta font-bold text-[20px]'>{data.totalPositiveSentiment}</div>
              </div>
              <div>
                <div className='text-[#F59E0B] text-center font-normal text-[14px] font-plus-jakarta mb-2'>Neutral</div>
                <div className='text-center text-[#1E293B] dark:text-white font-plus-jakarta font-bold text-[20px]'>{data.totalNeutralSentiment}</div>
              </div>
              <div>
                <div className='text-[#EF4444] text-center font-normal text-[14px] font-plus-jakarta mb-2'>Negative</div>
                <div className='text-center text-[#1E293B] dark:text-white font-plus-jakarta font-bold text-[20px]'>{data.totalNegativeSentiment}</div>
              </div>
            </div>
          </StatCard>
        </div>

        {/* Column 2: Status (bars) */}
        <StatCard title='Status'>
          <ProgressBar
            label='Allocated & Acknowledged'
            count={data.totalAllocatedAndAcknowledged}
            pct={data.totalAllocatedAndAcknowledgedPct}
            color='#414651'
            maxPct={statusMaxPct}
          />
          <ProgressBar
            label='Allocated but Not Acknowledged'
            count={data.totalAllocatedButNotAcknowledged}
            pct={data.totalAllocatedButNotAcknowledgedPct}
            color='#414651'
            maxPct={statusMaxPct}
          />
          <ProgressBar
            label='Unallocated'
            count={data.totalUnallocated}
            pct={data.totalUnallocatedPct}
            color='#414651'
            maxPct={statusMaxPct}
          />
        </StatCard>

        {/* Column 3: Sentiment of Feedback (bars) */}
        <StatCard title='Sentiment of Feedback'>
          <ProgressBar
            label='Positive'
            count={data.totalPositiveSentiment}
            pct={data.positiveSentimentPct}
            color='#10B981'
            maxPct={sentBarMaxPct}
          />
          <ProgressBar
            label='Neutral'
            count={data.totalNeutralSentiment}
            pct={data.neutralSentimentPct}
            color='#F59E0B'
            maxPct={sentBarMaxPct}
          />
          <ProgressBar
            label='Negative'
            count={data.totalNegativeSentiment}
            pct={data.negativeSentimentPct}
            color='#EF4444'
            maxPct={sentBarMaxPct}
          />
        </StatCard>
      </div>
    </div>
  );
};


const SectionCard: React.FC<{
  title: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}> = ({ title, children, right }) => (
  <div className='rounded-2xl border p-4 shadow-sm bg-white dark:bg-[#111317] dark:border-[#252B37]'>
    <div className='mb-3 flex items-center justify-between'>
      <h2 className='text-lg font-arial font-bold text-[16px] text-[#1E293B] dark:text-[#E2E8F0]'>{title}</h2>
      {right}
    </div>
    {children}
  </div>
);

interface ExecFilters {
  itinerary?: string;
  startDate?: string;
  endDate?: string;
  trainManagerId?: string;
  crewMemberId?: string;
  crewMemberRole?: string;
}

interface RebeccaTopicsDashboardProps {
  trainManagers?: { label: string; value: string; [k: string]: any }[];
  staff?: { label: string; value: string; [k: string]: any }[];
  execFilters?: ExecFilters;
}

export default function RebeccaTopicsDashboard({ trainManagers, staff, execFilters }: RebeccaTopicsDashboardProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { theme } = useTheme();

  // slice state
  const taxonomy = useSelector((s: RootState) => s.feedbackTopics.taxonomy) as Taxonomy | null;

  const total = useSelector((s: RootState) => s.feedbackTopics.total) || 0;
  const searchLoading = useSelector((s: RootState) => s.feedbackTopics.searchLoading);

  const stats = useSelector((s: RootState) => s.feedbackTopics.stats) as AssignmentStats | null;

  // exec by category
  const execByCategory = useSelector((s: RootState) => s.feedbackTopics.execByCategory) as ExecByCategoryData | null;
  const execByCategoryLoading = useSelector((s: RootState) => s.feedbackTopics.execByCategoryLoading);

  // Donut chart: selected category and subcategory for drill-down
  const [selectedChartCategory, setSelectedChartCategory] = useState<string | null>(null);
  const [selectedChartSubcategory, setSelectedChartSubcategory] = useState<string | null>(null);

  // filters
  const [category, setCategory] = useState<string>('');
  const [subCategory, setSubCategory] = useState<string>('');
  const [allCategories, setAllCategories] = useState<boolean>(false);
  const [allSubcategories, setAllSubcategories] = useState<boolean>(true);
  const [unassignedOnly, setUnassignedOnly] = useState<boolean>(false);
  const [selectedTrainManager, setSelectedTrainManager] = useState<any>(null);
  const [selectedCrewMember, setSelectedCrewMember] = useState<any>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [size] = useState<number>(20);

  // infinite scroll
  const [accumulatedTopics, setAccumulatedTopics] = useState<FeedbackDoc[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);

  // filter panel open/closed
  const [filtersOpen, setFiltersOpen] = useState(true);

  // taxonomy on mount
  useEffect(() => {
    dispatch(tryToFetchTopicTaxonomy());
  }, [dispatch]);

  // allowed subcategories
  const allowedSubs = useMemo(() => {
    if (!taxonomy) return [] as string[];
    return taxonomy.subcategories[category] || [];
  }, [taxonomy, category]);

  // keep subcategory valid
  useEffect(() => {
    if (category === '' || allSubcategories) {
      setSubCategory('');
    } else if (subCategory && !allowedSubs.includes(subCategory)) {
      setSubCategory('');
    }
  }, [category, allSubcategories, allowedSubs, subCategory]);

  // fetch topics thunk
  const fetchTopics = useCallback(
    async (pageNum: number, append: boolean) => {
      loadingMoreRef.current = true;
      try {
        const result = await dispatch(
          tryToSearchFeedbackTopics({
            pageNumber: pageNum,
            pageSize: size,
            trainManagerId: selectedTrainManager?.value || undefined,
            crewMemberId: selectedCrewMember?.value || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            allCategories,
            allSubcategories,
            unassignedOnly,
            category: !allCategories && category ? category : undefined,
            subCategory: !allSubcategories && subCategory ? subCategory : undefined,
          })
        ).unwrap();

        const newItems = (result as any)?.data ?? [];
        const totalItems = (result as any)?.total ?? 0;

        if (append) {
          setAccumulatedTopics((prev) => [...prev, ...newItems]);
        } else {
          setAccumulatedTopics(newItems);
        }
        setHasMore(pageNum * size < totalItems);
      } catch {
        // error handled by slice
      } finally {
        loadingMoreRef.current = false;
      }
    },
    [
      dispatch,
      size,
      selectedTrainManager,
      selectedCrewMember,
      startDate,
      endDate,
      allCategories,
      allSubcategories,
      unassignedOnly,
      category,
      subCategory,
    ]
  );

  // Initial fetch & reset on filter change
  useEffect(() => {
    setPage(1);
    setAccumulatedTopics([]);
    setHasMore(true);
    fetchTopics(1, false);
  }, [fetchTopics]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || !hasMore || loadingMoreRef.current || searchLoading) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 40) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchTopics(nextPage, true);
    }
  }, [hasMore, searchLoading, page, fetchTopics]);

  // Apply filters (reset and re-fetch)
  const applyFilters = useCallback(() => {
    setPage(1);
    setAccumulatedTopics([]);
    setHasMore(true);
    fetchTopics(1, false);
  }, [fetchTopics]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setCategory('');
    setSubCategory('');
    setAllCategories(false);
    setAllSubcategories(true);
    setUnassignedOnly(false);
    setSelectedTrainManager(null);
    setSelectedCrewMember(null);
    setStartDate('');
    setEndDate('');
  }, []);

  // stats thunk
  const fetchStats = useCallback(() => {
    dispatch(tryToGetFeedbackTopicStats({}));
  }, [dispatch]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // exec by category thunk
  const fetchExecByCategory = useCallback(() => {
    const body: Record<string, any> = {};
    if (execFilters?.itinerary) body.itinerary = execFilters.itinerary;
    if (execFilters?.startDate) body.startDate = execFilters.startDate;
    if (execFilters?.endDate) body.endDate = execFilters.endDate;
    if (execFilters?.trainManagerId) body.trainManagerId = execFilters.trainManagerId;
    if (execFilters?.crewMemberId) body.crewMemberId = execFilters.crewMemberId;
    if (execFilters?.crewMemberRole) body.crewMemberRole = execFilters.crewMemberRole;
    dispatch(tryToFetchFeedbackExecByCategory(body));
  }, [dispatch, execFilters]);

  useEffect(() => {
    fetchExecByCategory();
  }, [fetchExecByCategory]);

  // colors - matching exact taxonomy categories
  const CATEGORY_COLORS: Record<string, string> = {
    Cabin: '#22c55e',           // green
    Restaurant: '#f59e0b',      // amber
    Bar: '#06b6d4',             // cyan
    Lounge: '#8b5cf6',          // violet
    Boarding: '#ec4899',        // pink
    'Onboard Experience': '#0ea5e9',   // sky blue
    'Offboard Experience': '#14b8a6',  // teal
    Service: '#f97316',         // orange
    'General Experience': '#6366f1',   // indigo
    Unassigned: '#9ca3af',      // gray
  };

  function tint(hex: string, t = 0.25) {
    const n = hex.replace('#', '');
    const num = parseInt(n, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    const mix = (c: number) => Math.round(c + (255 - c) * t);
    return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
  }

  // ── Donut data — uses stats (assignment-stats) as primary for top level ──
  const donutData = useMemo(() => {
    // Level 1: top-level categories — prefer stats from assignment-stats
    if (!selectedChartCategory) {
      if (stats) {
        const slices = stats.categories.map((c) => {
          const name = c.category ?? 'Unassigned';
          const base = CATEGORY_COLORS[name] || CATEGORY_COLORS.Unassigned;
          return { name, value: c.count, fill: base, category: c.category ?? undefined };
        });
        if (stats.unassigned.count > 0) {
          slices.push({
            name: 'Unassigned',
            value: stats.unassigned.count,
            fill: CATEGORY_COLORS.Unassigned,
            category: 'Unassigned',
          });
        }
        return slices;
      }
      if (execByCategory) {
        const slices = execByCategory.categories.map((c) => {
          const base = CATEGORY_COLORS[c.category] || CATEGORY_COLORS.Unassigned;
          return { name: c.category, value: c.total, fill: base, category: c.category };
        });
        if (execByCategory.unassigned.total > 0) {
          slices.push({
            name: 'Unassigned',
            value: execByCategory.unassigned.total,
            fill: CATEGORY_COLORS.Unassigned,
            category: 'Unassigned',
          });
        }
        return slices;
      }
      return [] as { name: string; value: number; fill: string; category?: string }[];
    }

    // Level 2: subcategory drill-down — prefer stats, fallback to execByCategory
    if (selectedChartCategory === 'Unassigned') {
      const count = stats?.unassigned?.count ?? execByCategory?.unassigned?.total ?? 0;
      return [{ name: 'Unassigned', value: count, fill: CATEGORY_COLORS.Unassigned }];
    }

    // Try stats subcategories first
    if (stats) {
      const categorySubs = stats.subcategories.filter(
        (s) => s.category === selectedChartCategory && s.subCategory !== null,
      );
      const base = CATEGORY_COLORS[selectedChartCategory] || CATEGORY_COLORS.Unassigned;
      if (categorySubs.length === 0) {
        const categoryData = stats.categories.find((c) => c.category === selectedChartCategory);
        return [{ name: selectedChartCategory, value: categoryData?.count ?? 0, fill: base }];
      }
      return categorySubs.map((s, idx) => {
        const t = Math.min(0.6, 0.08 + idx * 0.12);
        return { name: s.subCategory ?? 'Unknown', value: s.count, fill: tint(base, t), subCategory: s.subCategory };
      });
    }

    // Fallback to execByCategory for subcategory drill-down
    if (execByCategory) {
      const selectedCat = execByCategory.categories.find((c) => c.category === selectedChartCategory);
      if (!selectedCat) return [];
      const base = CATEGORY_COLORS[selectedChartCategory] || CATEGORY_COLORS.Unassigned;
      if (!selectedCat.subcategories || selectedCat.subcategories.length === 0) {
        return [{ name: selectedChartCategory, value: selectedCat.total, fill: base }];
      }
      return selectedCat.subcategories.map((s, idx) => {
        const t = Math.min(0.6, 0.08 + idx * 0.12);
        return { name: s.subCategory ?? 'Unknown', value: s.total, fill: tint(base, t), subCategory: s.subCategory };
      });
    }

    return [];
  }, [execByCategory, stats, selectedChartCategory]);

  // Center value for donut — prefer stats (assignment-stats) for top level
  const centerValue = useMemo(() => {
    if (!selectedChartCategory) {
      if (stats) return { count: stats.total, label: 'TOPICS' };
      if (execByCategory) return { count: execByCategory.overall.total, label: 'TOPICS' };
      return { count: 0, label: 'TOPICS' };
    }
    if (selectedChartCategory === 'Unassigned') {
      const count = stats?.unassigned?.count ?? execByCategory?.unassigned?.total ?? 0;
      return { count, label: 'FEEDBACKS' };
    }
    // Category selected — find count from stats or execByCategory
    if (stats) {
      const categoryData = stats.categories.find((c) => c.category === selectedChartCategory);
      if (categoryData) return { count: categoryData.count, label: 'FEEDBACKS' };
    }
    if (execByCategory) {
      const cat = execByCategory.categories.find((c) => c.category === selectedChartCategory);
      return { count: cat?.total ?? 0, label: 'FEEDBACKS' };
    }
    return { count: 0, label: 'FEEDBACKS' };
  }, [execByCategory, stats, selectedChartCategory]);

  // Format number for display (e.g., 1500 -> 1.5k)
  const formatCount = (count: number) => {
    if (count >= 1000) {
      return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    return count.toString();
  };

  // Handle donut segment click
  const handleDonutClick = (data: any) => {
    if (!selectedChartCategory) {
      // Clicking on a category — drill down
      if (data?.name) {
        setSelectedChartCategory(data.name);
        setSelectedChartSubcategory(null);
      }
    } else {
      // Clicking on a subcategory segment — select it
      if (data?.name && data.name !== selectedChartCategory) {
        setSelectedChartSubcategory(data.name);
      }
    }
  };

  // Current stats to show in the Assignment Stats cards
  const activeExecStats: ExecStatsFields | null = useMemo(() => {
    if (!execByCategory) return null;

    // Subcategory level
    if (selectedChartCategory && selectedChartSubcategory) {
      if (selectedChartCategory === 'Unassigned') return execByCategory.unassigned;
      const cat = execByCategory.categories.find((c) => c.category === selectedChartCategory);
      const sub = cat?.subcategories?.find((s) => s.subCategory === selectedChartSubcategory);
      return sub ?? null;
    }

    // Category level
    if (selectedChartCategory) {
      if (selectedChartCategory === 'Unassigned') return execByCategory.unassigned;
      const cat = execByCategory.categories.find((c) => c.category === selectedChartCategory);
      return cat ?? null;
    }

    // Overall
    return execByCategory.overall;
  }, [execByCategory, selectedChartCategory, selectedChartSubcategory]);

  // Reset subcategory when category changes
  useEffect(() => {
    setSelectedChartSubcategory(null);
  }, [selectedChartCategory]);

  // Custom center label component
  const CenterLabel: React.FC<{ viewBox?: { cx: number; cy: number } }> = ({ viewBox }) => {
    const { cx, cy } = viewBox || { cx: 0, cy: 0 };
    return (
      <g>
        <text
          x={cx}
          y={cy - 8}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-gray-800 dark:fill-gray-200"
          style={{ fontSize: '28px', fontWeight: 600 }}
        >
          {formatCount(centerValue.count)}
        </text>
        <text
          x={cx}
          y={cy + 16}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-gray-500"
          style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.5px' }}
        >
          {centerValue.label}
        </text>
      </g>
    );
  };

  return (
    <div className='mx-auto pb-6 space-y-6'>
      {/* Taxonomy */}
      {/* <SectionCard
        title='Taxonomy'
        right={
          <div className='flex items-center gap-2'>
            {taxLoading ? (
              <Badge>Loading…</Badge>
            ) : taxError ? (
              <Badge>⚠️ {taxError}</Badge>
            ) : (
              <Badge>{taxonomy ? 'Loaded' : '—'}</Badge>
            )}
            <button
              className='rounded-lg border px-3 py-1 text-sm hover:shadow transition'
              onClick={() => setTaxOpen((v) => !v)}
              disabled={taxLoading || !!taxError || !taxonomy}
            >
              {taxOpen ? 'Hide' : 'Show'}
            </button>
          </div>
        }
      >
        {!taxonomy ? (
          <div className='text-sm text-gray-500'>No taxonomy loaded.</div>
        ) : !taxOpen ? (
          <div className='text-sm text-gray-500'>
            Click &quot;Show&quot; to view categories and subcategories.
          </div>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 max-h-72 overflow-auto pr-1'>
            {taxonomy.categories.map((catItem) => (
              <div key={catItem} className='rounded-lg border p-3'>
                <div className='font-medium mb-1'>{catItem}</div>
                <div className='text-sm text-gray-600'>
                  {(taxonomy.subcategories[catItem] || []).length > 0 ? (
                    <ul className='list-disc pl-5'>
                      {taxonomy.subcategories[catItem].map((s) => (
                        <li key={s}>{s}</li>
                      ))}
                    </ul>
                  ) : (
                    <em>No subcategories</em>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard> */}

      {/* Overview — 4 stat cards */}
      <div>
        <h2 className='text-xl font-bold text-gray-900 dark:text-white mb-4 mt-6'>Overview</h2>
        {execByCategory?.overall ? (
          <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
            {/* Column 1: Total Feedback + Sentiment counts stacked */}
            <div className='flex flex-col gap-4'>
              <div className='rounded-[14px] border-[#F5F5F5] border-[1.2px] bg-white p-5 dark:border-white/10 dark:bg-[#252B37]'>
                <div className='text-[#717680] dark:text-white !font-arial font-normal text-[13px] mb-6'>Total Feedback</div>
                <div className='flex justify-between'>
                  <div>
                    <div className='text-center font-plus-jakarta dark:text-white text-[#1E293B font-bold text-[24px]'>{execByCategory.overall.totalByGuest}</div>
                    <div className='text-xs text-center text-[10px] font-plus-jakarta font-medium dark:text-white text-[#717680]'>By Guest</div>
                  </div>
                  <div>
                    <div className='text-center font-plus-jakarta dark:text-white text-[#1E293B font-bold text-[24px]'>{execByCategory.overall.totalByStaff}</div>
                    <div className='text-xs text-center text-[10px] font-plus-jakarta font-medium dark:text-white text-[#717680]'>By Staff</div>
                  </div>
                </div>
              </div>

              <div className='rounded-[14px] border-[#F5F5F5] border-[1.2px] bg-white p-5 dark:border-white/10 dark:bg-[#252B37]'>
                <div className='text-[#717680] dark:text-white !font-arial font-normal text-[13px] mb-6'>Sentiment of Feedback</div>
                <div className='flex justify-between'>
                  <div>
                    <div className=' text-[#00C950] text-center font-normal text-[14px] font-plus-jakarta mb-2'>Positive</div>
                    <div className='text-center dark:text-white text-[#1E293B font-plus-jakarta font-bold text-[20px]'>{execByCategory.overall.totalPositiveSentiment}</div>
                  </div>
                  <div>
                    <div className='text-[#F59E0B] text-center font-normal text-[14px] font-plus-jakarta mb-2'>Neutral</div>
                    <div className='text-center dark:text-white text-[#1E293B font-plus-jakarta font-bold text-[20px]'>{execByCategory.overall.totalNeutralSentiment}</div>
                  </div>
                  <div>
                    <div className='text-[#EF4444] text-center font-normal text-[14px] font-plus-jakarta mb-2'>Negative</div>
                    <div className='text-center dark:text-white text-[#1E293B font-plus-jakarta font-bold text-[20px]'>{execByCategory.overall.totalNegativeSentiment}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Column 2: Status (bars) */}
            <div className='rounded-[14px] border-[#F5F5F5] border-[1.2px] bg-white p-5 dark:border-white/10 dark:bg-[#252B37]'>
              <div className='text-[#717680] dark:text-white !font-arial font-normal text-[13px] mb-6'>Status</div>
              <ProgressBar
                label='Allocated & Acknowledged'
                count={execByCategory.overall.totalAllocatedAndAcknowledged}
                pct={execByCategory.overall.totalAllocatedAndAcknowledgedPct}
                color={'#414651'}
                maxPct={Math.max(
                  execByCategory.overall.totalAllocatedAndAcknowledgedPct,
                  execByCategory.overall.totalAllocatedButNotAcknowledgedPct,
                  execByCategory.overall.totalUnallocatedPct,
                )}
              />
              <ProgressBar
                label='Allocated but Not Acknowledged'
                count={execByCategory.overall.totalAllocatedButNotAcknowledged}
                pct={execByCategory.overall.totalAllocatedButNotAcknowledgedPct}
                color={'#414651'}
                maxPct={Math.max(
                  execByCategory.overall.totalAllocatedAndAcknowledgedPct,
                  execByCategory.overall.totalAllocatedButNotAcknowledgedPct,
                  execByCategory.overall.totalUnallocatedPct,
                )}
              />
              <ProgressBar
                label='Unallocated'
                count={execByCategory.overall.totalUnallocated}
                pct={execByCategory.overall.totalUnallocatedPct}
                color={'#414651'}
                maxPct={Math.max(
                  execByCategory.overall.totalAllocatedAndAcknowledgedPct,
                  execByCategory.overall.totalAllocatedButNotAcknowledgedPct,
                  execByCategory.overall.totalUnallocatedPct,
                )}
              />
            </div>

            {/* Column 3: Sentiment of Feedback (bars) */}
            <div className='rounded-[14px] border-[#F5F5F5] border-[1.2px] bg-white p-5 dark:border-white/10 dark:bg-[#252B37]'>
              <div className='text-[#717680] dark:text-white !font-arial font-normal text-[13px] mb-6'>Sentiment of Feedback</div>
              <ProgressBar
                label='Positive'
                count={execByCategory.overall.totalPositiveSentiment}
                pct={execByCategory.overall.positiveSentimentPct}
                color='#10b981'
                maxPct={Math.max(
                  execByCategory.overall.positiveSentimentPct,
                  execByCategory.overall.neutralSentimentPct,
                  execByCategory.overall.negativeSentimentPct,
                )}
              />
              <ProgressBar
                label='Neutral'
                count={execByCategory.overall.totalNeutralSentiment}
                pct={execByCategory.overall.neutralSentimentPct}
                color='#f59e0b'
                maxPct={Math.max(
                  execByCategory.overall.positiveSentimentPct,
                  execByCategory.overall.neutralSentimentPct,
                  execByCategory.overall.negativeSentimentPct,
                )}
              />
              <ProgressBar
                label='Negative'
                count={execByCategory.overall.totalNegativeSentiment}
                pct={execByCategory.overall.negativeSentimentPct}
                color='#ef4444'
                maxPct={Math.max(
                  execByCategory.overall.positiveSentimentPct,
                  execByCategory.overall.neutralSentimentPct,
                  execByCategory.overall.negativeSentimentPct,
                )}
              />
            </div>
          </div>
        ) : (
          <div className='rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-400'>
            {execByCategoryLoading ? 'Loading overview…' : 'No data available.'}
          </div>
        )}
      </div>

      {/* Topics — title outside card */}
      <div>
        <h2 className='text-xl font-bold text-gray-900 dark:text-white mb-4'>Topics</h2>

        <div className='rounded-2xl border bg-white dark:bg-[#111317] dark:border-[#252B37] shadow-sm p-5 space-y-4'>
          {/* Results count */}
          <div className='text-sm text-gray-500'>{total} results</div>

          {/* Sub-card 1: Filters */}
          <div className='rounded-xl border border-gray-200 bg-white dark:bg-[#111317] dark:border-[#252B37] overflow-hidden'>
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className='flex w-full items-center dark:bg-[#232323] justify-between px-6 py-3.5 text-sm font-semibold text-gray-900 hover:text-gray-800 transition'
              style={{ backgroundColor: theme === 'dark' ? '#232323' : '#FAFAFA', borderBottom: theme === 'dark' ? '1px solid #ffffff1a' : '1px solid #F5F5F5' }}
            >
              <span className='flex items-center gap-2 dark:text-white'>
                <img src="/svg/filter_icon.svg" className="w-[15px] h-[15px] flex-shrink-0 dark:contrast-0" alt="filter" />
                {/* <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z' />
                </svg> */}
                Apply Filters
              </span>
              {filtersOpen ? (
                <svg className='h-6 w-6 dark:contrast-0' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  {/* Open: top arrow points down, bottom arrow points up — pointing at each other */}
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 4l5 4 5-4' />
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 20l5-4 5 4' />
                </svg>
              ) : (
                <svg className='h-6 w-6 dark:contrast-0' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  {/* Closed: top arrow points up, bottom arrow points down — pointing away */}
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 10l5-4 5 4' />
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 14l5 4 5-4' />
                </svg>
              )}
            </button>

            <div
              className='overflow-hidden transition-all duration-300 ease-in-out'
              style={{
                maxHeight: filtersOpen ? '600px' : '0px',
                opacity: filtersOpen ? 1 : 0,
              }}
            >
              <div className='px-6 py-5 space-y-5'>
                {/* Row 1: Category, Subcategory */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-xs font-medium text-gray-600 dark:text-white mb-1'>Category</label>
                    <select
                      className='w-full rounded-sm border border-gray-300 dark:border-white/10 px-3 py-2 text-sm bg-white dark:text-white dark:bg-[#333333] focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                      value={allCategories ? '__all__' : category}
                      onChange={(e) => {
                        if (e.target.value === '__all__') {
                          setAllCategories(true);
                          setCategory('');
                        } else {
                          setAllCategories(false);
                          setCategory(e.target.value);
                        }
                      }}
                    >
                      <option value='__all__'>All Categories</option>
                      {taxonomy?.categories.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className='block text-xs font-medium text-gray-600 dark:text-white mb-1'>Subcategory</label>
                    <select
                      className='w-full rounded-sm border border-gray-300 dark:border-white/10 px-3 py-2 text-sm bg-white dark:text-white dark:bg-[#333333] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400'
                      value={allSubcategories ? '__all__' : subCategory}
                      onChange={(e) => {
                        if (e.target.value === '__all__') {
                          setAllSubcategories(true);
                          setSubCategory('');
                        } else {
                          setAllSubcategories(false);
                          setSubCategory(e.target.value);
                        }
                      }}
                      disabled={allCategories || !category || allowedSubs.length === 0}
                    >
                      <option value='__all__'>All Subcategories</option>
                      {allowedSubs.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 2: Train Manager ID, Crew Member ID, Start Date, End Date */}
                <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'>
                  <SelectCheckbox
                    name='Train Manager'
                    options={trainManagers || []}
                    selectedOption={selectedTrainManager}
                    uniqueName='trainManager'
                    onChangeSelectedOption={(e) => setSelectedTrainManager(e)}
                    placeholder='Search by name'
                    containerStyle='mb-0 w-full'
                  />

                  <SelectCheckbox
                    name='Crew Member'
                    options={staff || []}
                    selectedOption={selectedCrewMember}
                    uniqueName='crewMember'
                    onChangeSelectedOption={(e) => setSelectedCrewMember(e)}
                    placeholder='Search by name'
                    containerStyle='mb-0 w-full'
                  />

                  <div className='mb-0 w-full'>
                    <p className='mb-1 text-[#0A0D12] dark:text-white !font-inter text-sm font-normal'>Start Date</p>
                    <input
                      type='date'
                      className='w-full rounded border border-[#E4E7EC] dark:border-white/10 px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-white dark:bg-[#333333] bg-white'
                      style={{ height: 42 }}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  <div className='mb-0 w-full'>
                    <p className='mb-1 text-[#0A0D12] dark:text-white !font-inter text-sm font-normal'>End Date</p>
                    <input
                      type='date'
                      className='w-full rounded border border-[#E4E7EC] dark:border-white/10 px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-white dark:bg-[#333333] bg-white'
                      style={{ height: 42 }}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>

                {/* Unassigned only toggle */}
                <div>
                  <span className='block text-sm text-gray-600 dark:text-white mb-1.5'>Unassigned only</span>
                  <button
                    type='button'
                    role='switch'
                    aria-checked={unassignedOnly}
                    onClick={() => setUnassignedOnly((v) => !v)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${unassignedOnly ? 'bg-gray-900' : 'bg-gray-200'
                      }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-[#333333] shadow ring-0 transition duration-200 ease-in-out ${unassignedOnly ? 'translate-x-5' : 'translate-x-0'
                        }`}
                    />
                  </button>
                </div>

                {/* Active filter pills + actions */}
                <div className='flex items-center justify-between flex-wrap gap-2'>
                  <div className='flex flex-wrap gap-2'>
                    {(allCategories || !category) && (
                      <span className='inline-flex items-center gap-1 rounded-md bg-gray-100 dark:bg-[#111317] px-3 py-1 text-xs text-gray-700 dark:text-white'>
                        Category: All Categories
                        <button
                          onClick={() => { setAllCategories(true); setCategory(''); }}
                          className='ml-0.5 hover:text-gray-900'
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {!allCategories && category && (
                      <span className='inline-flex items-center gap-1 rounded-md bg-blue-50 dark:bg-[#111317] px-3 py-1 text-xs text-blue-700 dark:text-white'>
                        Category: {category}
                        <button
                          onClick={() => { setCategory(''); setAllCategories(true); }}
                          className='ml-0.5 hover:text-blue-900'
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {(allSubcategories || !subCategory) && (
                      <span className='inline-flex items-center gap-1 rounded-md bg-gray-100 dark:bg-[#111317] px-3 py-1 text-xs text-gray-700 dark:text-white'>
                        Subcategory: All Subcategories
                        <button
                          onClick={() => { setAllSubcategories(true); setSubCategory(''); }}
                          className='ml-0.5 hover:text-gray-900'
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {!allSubcategories && subCategory && (
                      <span className='inline-flex items-center gap-1 rounded-md bg-blue-50 dark:bg-[#111317] px-3 py-1 text-xs text-blue-700 dark:text-white'>
                        Subcategory: {subCategory}
                        <button
                          onClick={() => { setSubCategory(''); setAllSubcategories(true); }}
                          className='ml-0.5 hover:text-blue-900'
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {selectedTrainManager && (
                      <span className='inline-flex items-center gap-1 rounded-md bg-blue-50 px-3 py-1 text-xs text-blue-700'>
                        Train Manager: {selectedTrainManager.label}
                        <button onClick={() => setSelectedTrainManager(null)} className='ml-0.5 hover:text-blue-900'>×</button>
                      </span>
                    )}
                    {selectedCrewMember && (
                      <span className='inline-flex items-center gap-1 rounded-md bg-blue-50 px-3 py-1 text-xs text-blue-700'>
                        Crew Member: {selectedCrewMember.label}
                        <button onClick={() => setSelectedCrewMember(null)} className='ml-0.5 hover:text-blue-900'>×</button>
                      </span>
                    )}
                    {startDate && (
                      <span className='inline-flex items-center gap-1 rounded-md bg-blue-50 px-3 py-1 text-xs text-blue-700'>
                        From: {startDate}
                        <button onClick={() => setStartDate('')} className='ml-0.5 hover:text-blue-900'>×</button>
                      </span>
                    )}
                    {endDate && (
                      <span className='inline-flex items-center gap-1 rounded-md bg-blue-50 px-3 py-1 text-xs text-blue-700'>
                        To: {endDate}
                        <button onClick={() => setEndDate('')} className='ml-0.5 hover:text-blue-900'>×</button>
                      </span>
                    )}
                    {unassignedOnly && (
                      <span className='inline-flex items-center gap-1 rounded-md bg-orange-50 px-3 py-1 text-xs text-orange-700'>
                        Unassigned only
                        <button onClick={() => setUnassignedOnly(false)} className='ml-0.5 hover:text-orange-900'>×</button>
                      </span>
                    )}
                  </div>

                  <div className='flex items-center gap-3'>
                    <button
                      onClick={clearAllFilters}
                      className='flex items-center gap-2 rounded-[10px] h-[35px] bg-[#F1F5F9] !font-bold bg-gray-50 px-5 py-2.5 text-sm font-medium text-[#1E293B] font-bold !text-[12px] w-[115px] font-plus-jakarta hover:bg-gray-100 transition hover:bg-gray-100 transition'
                    >
                      <img src='/svg/clear.svg' className='w-[12px] h-[12px] flex-shrink-0' alt='trip' />
                      Clear All
                    </button>
                    <button
                      onClick={applyFilters}
                      className='  bg-[#1E293B] h-[35px] rounded-[10px] text-white text-sm font-medium hover:bg-gray-800 transition !text-[12px] w-[137px] font-plus-jakarta'
                      disabled={searchLoading}
                    >
                      Show
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sub-card 2: Table */}
          <div className='rounded-xl border border-gray-200 bg-white dark:bg-[#111317] dark:border-[#252B37] overflow-hidden'>
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className='overflow-auto'
              style={{ maxHeight: '520px' }}
            >
              <table className='min-w-full text-sm'>
                <thead className='sticky top-0 bg-gray-50 dark:bg-[#232323] z-10'>
                  <tr className='border-b dark:border-[#252B37]'>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider'>Topic</th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider'>Category</th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider'>Subcategory</th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider'>Trip</th>
                    <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider'>Created at:</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100 dark:divide-white/10'>
                  {accumulatedTopics.length === 0 && !searchLoading ? (
                    <tr>
                      <td colSpan={5} className='px-6 py-10 text-center text-gray-400 dark:text-white'>
                        No topics found.
                      </td>
                    </tr>
                  ) : (
                    accumulatedTopics.map((t, idx) => {
                      const cat = t.additionalInfo?.category ?? null;
                      const sub = t.additionalInfo?.subCategory ?? '—';
                      const trip = t.additionalInfo?.journeyTitle ?? '—';
                      const dateStr = t.createdAt
                        ? new Date(t.createdAt).toLocaleDateString('en-GB', {
                          timeZone: 'Europe/Rome',
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        }) +
                        '\n' +
                        new Date(t.createdAt).toLocaleTimeString('en-GB', {
                          timeZone: 'Europe/Rome',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })
                        : '—';
                      return (
                        <tr key={`${t._id}-${idx}`} className='hover:bg-gray-50 hover:dark:bg-[#232323] transition-colors'>
                          <td className='px-6 py-3 text-sm text-gray-700 dark:text-white max-w-xs truncate'>
                            {t?.description || t._id}
                          </td>
                          <td className='px-6 py-3'>
                            {cat ? (
                              <span className='inline-block rounded-md border border-gray-200 bg-gray-50 dark:bg-[#111317] px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-white'>
                                {cat}
                              </span>
                            ) : (
                              <span className='text-gray-400 dark:text-white'>—</span>
                            )}
                          </td>
                          <td className='px-6 py-3 text-sm text-gray-700 dark:text-white'>{sub}</td>
                          <td className='px-6 py-3 text-sm text-gray-700 dark:text-white'>{trip}</td>
                          <td className='px-6 py-3 text-right text-sm text-gray-500 dark:text-white whitespace-pre-line'>
                            {dateStr}
                          </td>
                        </tr>
                      );
                    })
                  )}
                  {searchLoading && (
                    <tr>
                      <td colSpan={5} className='px-6 py-4 text-center text-gray-400 text-sm'>
                        Loading more…
                      </td>
                    </tr>
                  )}
                  {!hasMore && accumulatedTopics.length > 0 && (
                    <tr>
                      <td colSpan={5} className='px-6 py-3 text-center text-gray-300 text-xs'>
                        All results loaded
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <SectionCard title='Assignment stats'>
        {!stats && !execByCategory ? (
          <div className='text-gray-500'>
            {execByCategoryLoading ? 'Loading stats…' : 'No stats loaded.'}
          </div>
        ) : (
          <>
            <div className='flex flex-wrap'>
              {/* Left: Categories list */}
              <div className='flex-shrink-0 w-full lg:w-[350px] pr-6'>
                <div className=' text-[#0A0D12] dark:text-white font-arial font-normal text-[15px] mb-3'>Categories</div>
                <div className='grid grid-cols-2 gap-x-6 gap-y-2'>
                  {(() => {
                    // Use stats (assignment-stats) as primary source for this section
                    const catItems = stats
                      ? [
                        ...(stats.categories ?? []).map((c) => ({
                          name: c.category ?? 'Unassigned',
                          count: c.count,
                          pct: stats.total > 0 ? (c.count / stats.total) * 100 : 0,
                        })),
                        ...(stats.unassigned.count > 0
                          ? [{
                            name: 'Unassigned',
                            count: stats.unassigned.count,
                            pct: stats.total > 0 ? (stats.unassigned.count / stats.total) * 100 : 0,
                          }]
                          : []),
                      ]
                      : execByCategory
                        ? [
                          ...execByCategory.categories.map((c) => ({
                            name: c.category,
                            count: c.total,
                            pct: execByCategory.overall.total > 0
                              ? (c.total / execByCategory.overall.total) * 100
                              : 0,
                          })),
                          ...(execByCategory.unassigned.total > 0
                            ? [{
                              name: 'Unassigned',
                              count: execByCategory.unassigned.total,
                              pct: execByCategory.overall.total > 0
                                ? (execByCategory.unassigned.total / execByCategory.overall.total) * 100
                                : 0,
                            }]
                            : []),
                        ]
                        : [];

                    return catItems.map((c, idx) => {
                      const isSelected = selectedChartCategory === c.name;
                      const catColor = CATEGORY_COLORS[c.name] || CATEGORY_COLORS.Unassigned;
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            setSelectedChartCategory(c.name);
                            setSelectedChartSubcategory(null);
                          }}
                          className={`text-left group transition-all duration-200 p-2 rounded-[4px] ${isSelected ? 'font-medium bg-[#2D8EFF1A]' : selectedChartCategory ? 'opacity-50 hover:opacity-80' : ''}`}
                          style={isSelected ? { borderLeft: `3px solid ${catColor}`, paddingLeft: '5px' } : {}}
                        >
                          <div className={`font-arial font-normal text-[14px] group-hover:text-blue-600 ${isSelected ? 'text-[#0A0D12] dark:text-white' : 'text-[#0A0D12] dark:text-white/80'}`}>
                            {c.name}
                          </div>
                          <div className='text-[#717680] font-normal font-arial text-[13px]'>
                            {c.count} ({c.pct.toFixed(1)}%)
                          </div>
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Vertical divider */}
              <div className='hidden lg:block flex-shrink-0 self-center h-[400px] w-px bg-gray-200 mx-4' />

              {/* Middle: Subcategories (only when no category selected) */}
              {!selectedChartCategory && (
                <div className='flex-shrink-0 w-full lg:w-56 pl-2 mt-4 lg:mt-0'>
                  <div className='text-[#0A0D12] dark:text-white font-arial font-normal text-[15px] mb-3'>Subcategories</div>
                  <div className='text-sm text-gray-500'>
                    Click on a category to view subcategories
                  </div>
                </div>
              )}

              {/* Middle: Subcategories list (when category selected) */}
              {selectedChartCategory && (
                <div className='flex-shrink-0 w-full lg:w-56 pl-2 mt-4 lg:mt-0'>
                  <div className='text-[#0A0D12] dark:text-white font-arial font-normal text-[15px] mb-3'>Subcategories</div>
                  {(() => {
                    // Prefer stats subcategories, fallback to execByCategory
                    let subItems: any[];
                    if (selectedChartCategory === 'Unassigned') {
                      subItems = [];
                    } else if (stats) {
                      subItems = (stats.subcategories ?? []).filter(
                        (s) => s.category === selectedChartCategory && s.subCategory !== null,
                      );
                    } else if (execByCategory) {
                      subItems = execByCategory.categories
                        .find((c) => c.category === selectedChartCategory)
                        ?.subcategories?.filter((s: any) => s.subCategory !== null) ?? [];
                    } else {
                      subItems = [];
                    }

                    if (subItems.length === 0) {
                      return (
                        <div className='text-sm text-gray-500 italic'>
                          No subcategories
                        </div>
                      );
                    }

                    // Get category total for percentage calculation
                    const catTotal = stats
                      ? (stats.categories.find((c) => c.category === selectedChartCategory)?.count ?? 0)
                      : (execByCategory?.categories.find((c) => c.category === selectedChartCategory)?.total ?? 0);

                    return (
                      <div className='space-y-2'>
                        {subItems.map((s: any, idx: number) => {
                          const subName = s.subCategory;
                          const subCount = s.count ?? s.total ?? 0;
                          const isSubSelected = selectedChartSubcategory === subName;
                          const subColor = (() => {
                            const base = CATEGORY_COLORS[selectedChartCategory || ''] || CATEGORY_COLORS.Unassigned;
                            const t = Math.min(0.6, 0.08 + idx * 0.12);
                            return tint(base, t);
                          })();
                          const pctOfCat = catTotal > 0 ? ((subCount / catTotal) * 100).toFixed(1) : '0.0';
                          return (
                            <button
                              key={idx}
                              onClick={() => setSelectedChartSubcategory(subName)}
                              className={`text-left block w-full p-2 rounded-[4px] group transition-all duration-200 ${isSubSelected ? 'font-medium bg-[#2D8EFF1A]' : selectedChartSubcategory ? 'opacity-50 hover:opacity-80' : ''}`}
                              style={isSubSelected ? { borderLeft: `3px solid ${subColor}`, paddingLeft: '5px' } : {}}
                            >
                              <div className='text-sm text-gray-900 group-hover:text-blue-600 dark:text-white'>
                                {subName}
                              </div>
                              <div className='text-xs text-gray-500 dark:text-white/80'>
                                {subCount} ({pctOfCat}%)
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Right: Single ring donut chart */}
              <div className='flex-1 w-full lg:w-auto min-w-[250px] relative mt-4 lg:mt-0'>
                {/* All Categories link - positioned top left of chart area */}
                {selectedChartCategory && (
                  <button
                    onClick={() => {
                      setSelectedChartCategory(null);
                      setSelectedChartSubcategory(null);
                    }}
                    className='absolute top-0 left-0 text-sm text-blue-600 hover:text-blue-700 hover:underline z-10 dark:text-white'
                  >
                    All Categories
                  </button>
                )}
                <ResponsiveContainer width='100%' height={280}>
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey='value'
                      nameKey='name'
                      innerRadius={70}
                      outerRadius={110}
                      cx='50%'
                      cy='50%'
                      paddingAngle={1}
                      isAnimationActive={true}
                      animationDuration={300}
                      onClick={handleDonutClick}
                      style={{ cursor: 'pointer' }}
                    >
                      {donutData.map((entry, index) => {
                        // Determine if this segment is the "active" one
                        const isActive = selectedChartCategory
                          ? (selectedChartSubcategory
                            ? entry.name === selectedChartSubcategory
                            : entry.name === selectedChartCategory)
                          : false;

                        // If any segment is selected, dim the non-selected ones
                        const hasSelection = !!selectedChartCategory && !!selectedChartSubcategory;

                        return (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill}
                            stroke={isActive ? 'rgba(37, 43, 55, 0.5)' : '#fff'}
                            strokeWidth={isActive ? 3 : 2}
                            opacity={hasSelection && !isActive ? 0.4 : 1}
                            style={{ filter: isActive ? 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))' : 'none', transition: 'opacity 0.3s ease' }}
                          />
                        );
                      })}
                      <LabelList content={<CenterLabel />} position="center" />
                    </Pie>
                    <Tooltip
                      formatter={(value: any, name: any) => [value, name]}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ── Dashboard Cards (per category/subcategory) ── */}
            {activeExecStats ? (
              <CategoryExecCards
                data={activeExecStats}
                label={
                  selectedChartSubcategory
                    ? `${selectedChartCategory} › ${selectedChartSubcategory}`
                    : selectedChartCategory || 'Overall'
                }
              />
            ) : (
              <div className='mt-6 rounded-xl border bg-gray-50 p-6 text-center text-sm text-gray-500'>
                <div className='font-semibold mb-1'>Category Sentiment breakdown</div>
                Select a category above to see detailed breakdown
              </div>
            )}
          </>
        )}
      </SectionCard>
    </div>
  );
}
